<!--
  Typesense-backed federated search (KFT-4035, stage 2). Vue/@headlessui mirror
  of the @algolia/autocomplete-js FederatedSearch/index.vue, selected per-surface
  by useSearchEngine(); both live side by side for A/B testing (see
  docs/kft-4035-federated-search-typesense-plan.md).

  Lazy strategy: the input (desktop) / trigger button (mobile) render immediately
  so the search bar is visible and clickable on first paint — no readonly
  placeholder that swaps out (that swap lost focus and never opened the panel).
  Everything not needed until interaction is deferred out of the initial bundle:
  the 3-column panel is an async component chunk (loads on first open), and the
  Typesense data layer, GA `sendSearch` and the path helpers are dynamic imports
  that load on first search / selection.

  Desktop vs mobile is chosen by `useDevice().isMobile` (UA-based, so it is
  consistent between SSR and client — no hydration mismatch): only one branch is
  rendered/mounted, never both. Desktop is an inline input with a dropdown;
  mobile is a button that opens a full-screen overlay — the Vue equivalent of
  autocomplete-js' `--aa-detached-media-query`.
-->
<template>
  <!--
    Full width: no w-full, so the navbar's `mx-4` on this root shrinks it to the
    slot like the Algolia root (w-full + margin overflowed the slot by 32px).
  -->
  <div :class="{ 'w-full': !fullWidth }">
    <!-- Desktop inline input + dropdown (non-mobile UA, useDevice) -->
    <div v-if="!isMobile" ref="rootRef" class="relative">
      <Combobox
        v-model="selected"
        nullable
        immediate
        @update:model-value="onSelect"
      >
        <!--
          Full width (navbar) mirrors the Algolia navbar bar exactly (the
          `.fullwidth` rules in federated-search.css): grey-bordered pill,
          grey search icon on the left, clear button on the right, nudged up
          4.5px inside the 38px navbar slot. Hero (banner) keeps the teal
          submit button on the right.
        -->
        <form
          class="relative"
          :class="{ '-mt-[4.5px]': fullWidth }"
          @submit.prevent="onSubmit()"
        >
          <ComboboxInput
            :id="`${elementId}-input`"
            data-test-id="federated-search-input"
            :class="inputClass"
            :placeholder="placeholderText"
            :display-value="() => query"
            autocomplete="off"
            @change="onInput"
            @focus="onFocus"
            @blur="onBlur"
          />
          <template v-if="fullWidth">
            <button
              type="submit"
              :title="placeholderText"
              class="absolute left-0.5 top-1/2 flex h-[42px] w-11 -translate-y-1/2 items-center justify-center text-gray-400"
            >
              <IconSpinner
                v-if="isLoading"
                :size="18"
                class="animate-spin"
                aria-hidden="true"
              />
              <IconFederatedSearch
                v-else
                class="h-[18px] w-[18px]"
                aria-hidden="true"
              />
            </button>
            <button
              v-if="query"
              type="button"
              :title="t('search.selectLocalityComponent.clearSearchAria')"
              class="absolute right-px top-1/2 flex h-11 -translate-y-1/2 items-center px-[12.8px] text-gray-400"
              @mousedown.prevent
              @click="onClear"
            >
              <IconFederatedSearchClear
                class="h-[18px] w-5"
                aria-hidden="true"
              />
            </button>
          </template>
          <button
            v-else
            type="submit"
            :title="placeholderText"
            :disabled="submitDisabled"
            :aria-disabled="submitDisabled"
            class="absolute right-0.5 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-primary text-white duration-300 hover:bg-teal-900 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-primary"
          >
            <IconSpinner
              v-if="isLoading"
              :size="20"
              class="animate-spin"
              aria-hidden="true"
            />
            <IconSearch v-else :size="20" aria-hidden="true" />
          </button>
        </form>

        <!--
          Hero (default): the panel is wider than the input on purpose — the
          input keeps its limited hero-content width, while the results panel
          gets more room. Absolutely positioned from the input's left edge,
          overflowing right, capped to the viewport.
          Full width (navbar): like the Algolia `.aa-Panel-fullwidth`, a
          full-bleed sheet fixed under the navbar (57px + 16px offset), content
          centred at max-w-4xl by the panel itself, scrolling past 650px.
        -->
        <ComboboxOptions :class="panelClass">
          <FederatedSearchTypesensePanel
            v-bind="panelProps"
            :scroll="false"
            :max-locations="fullWidth ? 6 : 3"
            :max-categories="3"
            :max-events="4"
          />
        </ComboboxOptions>
      </Combobox>
    </div>

    <!--
      Mobile trigger button + full-screen overlay (mobile UA, useDevice).
      Full width (navbar): 2.2rem tall like the Algolia `.fullwidth`
      detached button, so the navbar row's pb-2 stays visible below it (at
      h-11 it filled the whole 44px row and ate the bottom padding).
    -->
    <template v-else>
      <button
        v-show="!detachedOpen"
        type="button"
        :title="placeholderText"
        data-test-id="federated-search-detached-button"
        class="flex w-full items-center gap-2 rounded-[2rem] border border-stone-300 bg-white px-3 text-stone-500"
        :class="fullWidth ? 'h-[2.2rem]' : 'h-11'"
        @click="openDetached"
      >
        <!-- Navbar: grey like the Algolia `.fullwidth` detached icon. -->
        <IconSearch
          :size="20"
          class="shrink-0"
          :class="fullWidth ? 'text-gray-500' : 'text-primary'"
          aria-hidden="true"
        />
        <!-- Decorative — the button's accessible name comes from :title above. -->
        <span class="truncate font-medium" aria-hidden="true">{{
          mobilePlaceholderText
        }}</span>
      </button>

      <Teleport to="body">
        <div
          v-if="detachedOpen"
          class="fixed inset-0 z-[999] flex flex-col bg-white"
        >
          <Combobox v-model="selected" nullable @update:model-value="onSelect">
            <form
              class="flex items-center gap-2 border-b border-stone-200 p-2"
              @submit.prevent="onSubmit()"
            >
              <IconSpinner
                v-if="isLoading"
                :size="20"
                class="ml-2 shrink-0 animate-spin text-stone-500"
                aria-hidden="true"
              />
              <IconSearch
                v-else
                :size="20"
                class="ml-2 shrink-0 text-stone-500"
                aria-hidden="true"
              />
              <ComboboxInput
                :id="`${elementId}-input-detached`"
                data-test-id="federated-search-input"
                class="h-11 flex-1 bg-white text-black placeholder:font-medium placeholder:text-stone-500 focus:outline-none"
                :placeholder="mobilePlaceholderText"
                :display-value="() => query"
                autocomplete="off"
                @change="onInput"
                @focus="onFocus"
                @blur="onBlur"
              />
              <button
                type="button"
                class="rounded px-3 py-1 text-sm text-stone-700"
                @click="closeDetached"
              >
                {{ t('search.buttons.cancel') }}
              </button>
            </form>

            <ComboboxOptions class="flex-1 overflow-y-auto">
              <FederatedSearchTypesensePanel v-bind="panelProps" />
            </ComboboxOptions>
          </Combobox>
        </div>
      </Teleport>
    </template>
  </div>
</template>

<script setup lang="ts">
import { searchPageQueryParams } from '~/utils/parseSearchIntent'
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onUnmounted,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { Combobox, ComboboxInput, ComboboxOptions } from '@headlessui/vue'
import { Search as IconSearch, Loader2 as IconSpinner } from 'lucide-vue-next'
import IconFederatedSearch from '~/assets/icons/icon-federated-search.svg?component'
import IconFederatedSearchClear from '~/assets/icons/icon-federated-search-clear.svg?component'
import { useDebounceFn, useEventListener } from '@vueuse/core'
import { useFederatedSearchTypesense } from './useFederatedSearchTypesense'
import { useMobilePlaceholderTypewriter } from './useMobilePlaceholderTypewriter'
import type { FederatedSearchOption } from './items'
import { createItemListViewSession } from './useTypesenseFederatedItemListViews'
import { useLastViewedEvents } from '~/composables/useLastViewedEvents'
import { useFederatedSearchAnalyticsSession } from '~/composables/algolia/useFederatedSearchAnalyticsSession'
import { useTypesenseGeoSort } from '~/composables/useTypesenseGeoSort'
import {
  navigateTo,
  useDevice,
  useLocalePath,
  useRoute,
  useRouter,
} from '#imports'

// The 3-column panel (markup + Map/MapPin/Package icons) is the bulk of the UI.
// Load it as its own chunk on first render (first time the dropdown opens), so
// the resting search bar stays light.
const FederatedSearchTypesensePanel = defineAsyncComponent(
  () => import('./FederatedSearchTypesensePanel.vue')
)

// `fullWidth` is the navbar layout (panel tracks the input width). `reduced`
// is accepted for parity with the Algolia component but not implemented yet;
// the banner uses neither.
const {
  elementId,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  reduced = false,
  fullWidth = false,
  analyticsPlacement = undefined,
} = defineProps<{
  elementId: string
  reduced?: boolean
  fullWidth?: boolean
  analyticsPlacement?: 'homepage' | 'navbar'
}>()

const { t, tm } = useI18n()
const inputClass = computed(() =>
  fullWidth
    ? 'h-12 w-full rounded-[2rem] border border-gray-200 bg-white px-10 text-black placeholder:font-medium placeholder:text-stone-500 focus:outline-none'
    : 'h-12 w-full rounded-[2rem] border border-stone-200 bg-white pl-4 pr-24 text-black placeholder:font-medium placeholder:text-stone-500 focus:border-primary-light focus:outline-none'
)

const panelClass = computed(() =>
  fullWidth
    ? 'fixed inset-x-0 top-[calc(73px+var(--notification-height,0px))] z-[21] max-h-[650px] w-full overflow-y-auto border border-[#f4f4f4] bg-white'
    : 'absolute left-0 top-full z-30 mt-2 w-[600px] max-w-[calc(100vw-2rem)] rounded-2xl border border-stone-200 bg-white shadow-lg md:w-[720px] lg:w-[820px]'
)

const placeholderText = t('search.algoliaAutocomplete.placeholder')

// UA-based (SSR-consistent, unlike a viewport media query): render only the
// branch for this device — the desktop inline dropdown or the mobile overlay,
// never both.
const { isMobile } = useDevice()

const router = useRouter()
const route = useRoute()
const localePath = useLocalePath()
const { setFederatedSearchAnalyticsOutcome } =
  useFederatedSearchAnalyticsSession()

const rootRef = ref<HTMLElement | null>(null)
const query = ref('')
const selected = ref<FederatedSearchOption | null>(null)
const detachedOpen = ref(false)
const isFocused = ref(false)

// Mobile placeholder: typed-in, cycling through a few phrases. Drives both
// the resting trigger button's label and the detached overlay input's native
// placeholder (same ref — the overlay input just renders whatever's typed so
// far, like any other placeholder). Desktop keeps the static placeholderText.
const mobilePlaceholdersRaw = tm(
  'search.federatedSearchTypesense.mobilePlaceholders'
) as string[] | undefined
const mobilePlaceholders = Array.isArray(mobilePlaceholdersRaw)
  ? mobilePlaceholdersRaw
  : [placeholderText]
const { text: mobilePlaceholderText } = useMobilePlaceholderTypewriter(
  mobilePlaceholders,
  isMobile
)

// Submitting to /search only makes sense with a query. The "show all results"
// CTA is the explicit browse-all path; the submit button and Enter require text.
// The disabled *state* only shows while the input is focused — at rest the
// search button looks normal rather than greyed out. Submitting empty is still
// blocked functionally by `onSubmit` regardless of focus.
const canSubmit = computed(() => query.value.trim().length > 0)
const submitDisabled = computed(() => isFocused.value && !canSubmit.value)

// KFT-4116: this component only ever renders on the Typesense engine (see
// BannerGallery.vue's `searchEngine === 'typesense'` gate), so geo can resolve
// unconditionally here — no engine check needed, unlike the Algolia component.
const { coords: geoCoords, resolveGeoCoords } = useTypesenseGeoSort()
if (import.meta.client) resolveGeoCoords()

const {
  locations,
  categories,
  events,
  eventsNbHits,
  resultsQuery,
  isLoading,
  runSearch,
} = useFederatedSearchTypesense({
  coords: () => geoCoords.value ?? undefined,
})

const { lastViewedEvents, loadLastViewedEvents } = useLastViewedEvents(false)
const hasLoadedLastViewedEvents = ref(false)
const isLastViewedEventsReady = ref(false)
// GA4 views sent during the current open — kept here so Clear's
// close + reopen (below) doesn't count as a new open
const itemListViewSession = createItemListViewSession()
const activated = ref(false)

const panelProps = computed(() => ({
  locations: locations.value,
  categories: categories.value,
  events: events.value,
  lastViewedEvents: lastViewedEvents.value,
  query: query.value,
  resultsQuery: resultsQuery.value,
  isLoading: isLoading.value,
  isLastViewedReady: isLastViewedEventsReady.value,
  analyticsPlacement,
  itemListViewSession,
}))

// First interaction: load last-viewed and fetch default (match-all) content so
// the panel opens populated, like the Algolia version. Runs once.
const ensureActivated = () => {
  if (activated.value) return
  activated.value = true
  if (!hasLoadedLastViewedEvents.value) {
    hasLoadedLastViewedEvents.value = true
    loadLastViewedEvents().finally(() => {
      isLastViewedEventsReady.value = true
    })
  }
  runSearch('')
}

// --- GA4 `search` event (debounced 1200ms), ported from index.vue ----------
let lastSentSearchTerm: string | null = null
let searchEventTimer: ReturnType<typeof setTimeout> | null = null

const cancelPendingSearchEvent = () => {
  if (searchEventTimer) {
    clearTimeout(searchEventTimer)
    searchEventTimer = null
  }
}

const scheduleAutocompleteSearchEvent = (q: string, count: number) => {
  cancelPendingSearchEvent()
  searchEventTimer = setTimeout(async () => {
    searchEventTimer = null
    const term = q.trim()
    if (!term || term === lastSentSearchTerm) return
    lastSentSearchTerm = term
    // GA helper loaded on demand (not needed until a search actually fires).
    const { sendSearch } = await import('~/composables/useGtmEvents')
    sendSearch(term, count)
  }, 1200)
}

// --- Query input -----------------------------------------------------------
const debouncedRun = useDebounceFn(async (q: string) => {
  // A superseded search was discarded: its term and count aren't what the
  // panel shows, and scheduling it would cancel the current query's event.
  const isLatest = await runSearch(q)
  if (isLatest && q.trim()) {
    scheduleAutocompleteSearchEvent(q, eventsNbHits.value ?? 0)
  }
}, 250)

const onInput = (event: Event) => {
  query.value = (event.target as HTMLInputElement).value
  keyboardNavigated = false
  debouncedRun(query.value)
}

// Open the combobox panel on focus. headlessui's `immediate` prop is a no-op in
// @headlessui/vue 1.7.23 (the Combobox context hardcodes immediate=false and
// never reads the prop), so we open the panel the way its own input handler
// does: a synthetic ArrowDown, which maps to `openCombobox()` while closed
// (no option gets highlighted). Guarded on aria-expanded so an already-open
// panel isn't stepped through.
const openPanel = (input: HTMLElement | null) => {
  if (!input || input.getAttribute('aria-expanded') === 'true') return
  input.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true })
  )
}

const onFocus = (event: FocusEvent) => {
  isFocused.value = true
  ensureActivated()
  openPanel(event.target as HTMLElement | null)
}

const onBlur = () => {
  isFocused.value = false
}

const openDetached = async () => {
  detachedOpen.value = true
  ensureActivated()
  await nextTick()
  // Open the panel without focusing the input, so the on-screen keyboard
  // doesn't cover the default content; it shows once the user taps the input.
  openPanel(document.getElementById(`${elementId}-input-detached`))
}

// Clear button (full-width bar only): reset the query, reload default content
// and keep the panel open with focus in the input, like the Algolia clear
// button. headlessui closes the panel on the button click, so reopen it.
const onClear = async () => {
  cancelPendingSearchEvent()
  query.value = ''
  const input = document.getElementById(
    `${elementId}-input`
  ) as HTMLInputElement | null
  if (input) {
    input.value = ''
    input.focus()
  }
  debouncedRun('')
  // headlessui already closed the panel on this click (its outside-click
  // handler runs first, in the capture phase); hold the GA4 view session so
  // the unmount + reopen isn't a new open
  itemListViewSession.isHeld = true
  try {
    await nextTick()
    openPanel(input)
    await nextTick()
  } finally {
    itemListViewSession.isHeld = false
  }
}

const closeDetached = () => {
  cancelPendingSearchEvent()
  detachedOpen.value = false
}

// --- Selection / submit ----------------------------------------------------
const blurInputs = () => {
  document.getElementById(`${elementId}-input`)?.blur()
  document.getElementById(`${elementId}-input-detached`)?.blur()
}

// `fromCta` is the "show all results" browse-all path, which is allowed with an
// empty query; the submit button / Enter are not (guarded by `canSubmit`).
const onSubmit = (fromCta = false) => {
  const q = query.value?.trim()
  if (!q && !fromCta) {
    // Clicking the resting search button with no query focuses the input
    // (opening the panel) rather than navigating to an empty /search.
    document.getElementById(`${elementId}-input`)?.focus()
    return
  }
  cancelPendingSearchEvent()
  setFederatedSearchAnalyticsOutcome('search_results')
  // Constraint phrases ("unter 50 euro", "am wochenende") become /search
  // filters, so the list shows what the dropdown showed (KFT-4145).
  navigateTo({
    path: localePath('/search/'),
    query: q ? searchPageQueryParams(q) : {},
  })
  detachedOpen.value = false
  blurInputs()
}

// Enter submits the typed query to /search?query=… unless the user arrowed to
// an option. headlessui 1.7.23 auto-highlights the first option whenever the
// panel opens, so left alone Enter would select that (often a location)
// instead. A native window capture listener runs before headlessui's input
// handler (a Vue `@keydown.capture` on the form doesn't: Vue's stale-handler
// timestamp guard can then skip headlessui's own listener). Only trusted keys
// count as navigation: `openPanel` dispatches a synthetic ArrowDown.
const NAV_KEYS = ['ArrowDown', 'ArrowUp', 'Home', 'End', 'PageUp', 'PageDown']
let keyboardNavigated = false
useEventListener(
  'keydown',
  (event: KeyboardEvent) => {
    const id = (event.target as HTMLElement | null)?.id
    if (id !== `${elementId}-input` && id !== `${elementId}-input-detached`)
      return
    if (NAV_KEYS.includes(event.key)) {
      if (event.isTrusted) keyboardNavigated = true
      return
    }
    if (event.key !== 'Enter' || event.isComposing || keyboardNavigated) return
    event.preventDefault()
    event.stopPropagation()
    onSubmit()
  },
  { capture: true }
)

const onSelect = async (value: FederatedSearchOption | null) => {
  if (!value) return
  cancelPendingSearchEvent()

  if (value.kind === 'event' || value.kind === 'lastViewed') {
    // GA helper loaded on demand; awaited so the list attribution is persisted
    // before the hard navigation below. Tracking must never block navigation
    // (e.g. a stale chunk after a deploy).
    try {
      const { sendTypesenseFederatedSelectItem } =
        await import('./gtmItemLists')
      sendTypesenseFederatedSelectItem(value, {
        events: events.value,
        lastViewedEvents: lastViewedEvents.value,
        query: query.value,
        placement: analyticsPlacement,
      })
    } catch (error) {
      console.error('[federated-search-typesense] select_item failed', error)
    }
  }

  switch (value.kind) {
    case 'location': {
      setFederatedSearchAnalyticsOutcome('location_results')
      // Path helpers (+ eventGetters) loaded on demand, only when a result is
      // actually selected — keeps them out of the initial bundle.
      const { mountUrlParamsFromItem } =
        await import('~/components/FederatedSearch/helpers')
      router.push(localePath(`/search/?${mountUrlParamsFromItem(value.item)}`))
      break
    }
    case 'category':
      setFederatedSearchAnalyticsOutcome('category_results')
      router.push(localePath(`/c/${value.item.slug}`))
      break
    case 'event': {
      const { getProductPath } =
        await import('~/components/FederatedSearch/helpers')
      // Typesense has no Algolia queryID, so no `?qid=` is appended.
      window.location.href = localePath(getProductPath(value.item))
      break
    }
    case 'lastViewed':
      window.location.href = localePath(`/e/${value.item.permalink}`)
      break
    case 'showAll':
      onSubmit(true)
      break
  }
  // Reset so selecting the same row again re-fires update:model-value.
  selected.value = null
}

watch(
  () => route.fullPath,
  () => {
    cancelPendingSearchEvent()
    detachedOpen.value = false
    blurInputs()
  }
)

onUnmounted(() => {
  cancelPendingSearchEvent()
})
</script>
